import { useEffect, useRef, useState } from 'react';
import StatusListItem from '@components/widgets/StatusListItem';
import {
  ACTIVITY_HISTORY_STEP,
  DETAIL_VIEW_ITEMS,
  DOCUMENT_LIST,
  ROADWORTHY_BY_STATE
} from '@constants';
import {
  AssetType,
  InspectionSecurityStatusEnum,
  InspectionStatusEnum,
  LenderDocumentTypeEnum,
  REGO_CERTIFICATE_DATA_EDIT_TYPE,
  BANK_STATEMENT_DATA_EDIT_TYPE
} from '@types';
import _reduce from 'lodash/reduce';
import {
  CircularProgressbarWithChildren,
  buildStyles
} from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { useInspectionDetailStore } from '@stores';
import SemiCircleProgress from '@components/widgets/SemiCircleProgressBar';
import { useMediaQuery } from 'usehooks-ts';
import clsx from 'clsx';
import { useLocation } from 'react-router-dom';
import _intersection from 'lodash/intersection';
import _isEqual from 'lodash/isEqual';
import { getAssetSpecificContent } from '@utils';
import { hasRequiredBankStatementFields } from '../../utils/bankStatementFields';
import {
  getInspectionCheckDocument,
  resolveSecurityCheckStatus,
  resolveMenuItemFlag,
  resolveVehicleDataEditsStatus,
  isVisibleSecurityCheck,
  aggregateSectionFlagSeverity,
  DETAIL_VIEW_FLAG_STATUS,
  getSectionIndicatorState,
  hasVehicleDetailsSectionDocument,
  shouldShowPayoutLetterSection,
  getDocumentOnlySectionFlag,
  getSectionCheckKeys
} from '../../utils/securityCheckHandlers';

type Props = {
  sendActiveStep: (step: string) => void;
  data: any;
  showActivityHistory?: boolean;
};
const DetailedInfo = ({
  sendActiveStep,
  data,
  showActivityHistory = false
}: Props) => {
  const isMobile = useMediaQuery('(max-width: 1140px)');
  const {
    setActivity,
    setCollapse,
    inspectionDetailData,
    activeStep,
    setActiveStep
  } = useInspectionDetailStore();
  const documentTypes = inspectionDetailData.documentTypes ?? [];
  const documents = inspectionDetailData.documents ?? {};
  const files = inspectionDetailData.files ?? [];
  const [countOfAll, setCountOfAll] = useState({
    done: inspectionDetailData.stepsComplete || 0,
    total: inspectionDetailData.stepsTotal || 0
  });
  
  // Store previous values for comparison
  const prevActiveStepRef = useRef<string>(activeStep);
  const prevDataRef = useRef<any>(data);
  
  useEffect(() => {
    // Compare with previous state
    const activeStepChanged = prevActiveStepRef.current !== activeStep;
    const prevData = prevDataRef.current || {};
    // For dataChanged, do a shallow comparison but skip inspectionChecks key
    const compareKeys = Object.keys({ ...(data || {}), ...(prevData || {}) }).filter((key) => key !== 'inspectionChecks');
    const dataChanged = compareKeys.some((key) => !_isEqual(prevData[key], data[key]));
    
    // Only proceed if there's an actual change
    if (activeStepChanged || dataChanged) {
      setActivity(false);
      sendActiveStep(activeStep);
      setCollapse(true);
      
      const totalObj = {
        done: inspectionDetailData.stepsComplete,
        total: inspectionDetailData.stepsTotal
      };
      setCountOfAll(totalObj);
      
      // Update refs with current values
      prevActiveStepRef.current = activeStep;
      prevDataRef.current = data;
    }
  }, [activeStep, data]);
  const location = useLocation();
  const DL_DOC_KEYS = [
    LenderDocumentTypeEnum.DRIVER_LICENCE_FRONT,
    LenderDocumentTypeEnum.DRIVER_LICENCE_BACK,
    LenderDocumentTypeEnum.FACE
  ] as const;

  const EXTRA_FIELD_MAP: Record<string, string> = {
    VIN: 'vin',
    REGO_PLATE: 'licencePlate',
    REGO_PAPERS: 'regoCertificate',
    BANK_STATEMENTS: 'bankStatement'
  };

  const countOfDoneElements = (index: number) => {
    if (location.pathname.includes('/demo/inspection')) {
      return _reduce(
        DOCUMENT_LIST[index],
        (result, value) => {
          if (documents[value]?.fileId) {
            result.done += 1;
          }
          if (['ROADWORTHY_CERT', 'PAYOUT_LETTER'].includes(value)) {
            if (documents[value]?.needsResubmit === false) {
              result.done += 1;
            }
          }
          result.total = DOCUMENT_LIST[index].length;
          return result;
        },
        { done: 0, total: 0 }
      );
    }

    // Identification (index 0): always counts DL front + back + face as 3 docs
    if (index === 0) {
      return {
        done: DL_DOC_KEYS.filter(
          (docKey) =>
            documents[docKey]?.fileId && !documents[docKey]?.needsResubmit
        ).length,
        total: 3
      };
    }

    return _reduce(
      _intersection(documentTypes, DOCUMENT_LIST[index]),
      (result, item) => {
        const extraField = EXTRA_FIELD_MAP[item];
        let hasFile =
          item === LenderDocumentTypeEnum.SALE_DETAILS
            ? inspectionDetailData?.extraFields?.saleDetails
                ?.vehicleSalePrice != null ||
              inspectionDetailData?.documents?.SALE_DETAILS?.extraFields
                ?.saleDetails?.vehicleSalePrice != null
            : extraField
              ? !!inspectionDetailData?.extraFields?.[extraField]
              : !!documents[item];

        if (['ROADWORTHY_CERT', 'PAYOUT_LETTER', 'MARINE_REGO_CERT'].includes(item)) {
          hasFile = documents[item]?.needsResubmit === false;
        }

        if (
          item === LenderDocumentTypeEnum.BANK_STATEMENTS &&
          !hasRequiredBankStatementFields(
            inspectionDetailData?.extraFields?.bankStatement
          )
        ) {
          hasFile = false;
        }

        if (documents[item]?.needsResubmit) {
          hasFile = false;
        }

        return {
          done: hasFile ? result.done + 1 : result.done,
          total: result.total + 1
        };
      },
      { done: 0, total: 0 }
    );
  };
  const registrationPaperStatus = files.some(
    (item: any) =>
      item.documentType === 'REGO_PAPERS' || item.documentType === 'MARINE_REGO_CERT'
  );

  const getDocumentObj = (items: any, item: any, key: any) =>
    getInspectionCheckDocument(
      items,
      key,
      item.value,
      inspectionDetailData.assetType
    );

  const flagStatus = (item: any, _index: number) => {
    const documentOnlyFlag = getDocumentOnlySectionFlag(
      item.value,
      inspectionDetailData.documents
    );
    if (documentOnlyFlag !== undefined) {
      return documentOnlyFlag;
    }

    const checkKeys = getSectionCheckKeys(
      item.value,
      inspectionDetailData.documentTypes
    );
    const flags = checkKeys
      .filter((key: string) =>
        isVisibleSecurityCheck(key, {
          documentTypes: inspectionDetailData.documentTypes,
          assetType: inspectionDetailData.assetType,
          inspectionChecks: inspectionDetailData.inspectionChecks,
          stepValue: item.value
        })
      )
      .map((key: any) => {
        const items = inspectionDetailData.inspectionChecks;
        const documentObj = getDocumentObj(items, item, key);
        let type = documentObj?.type;
        if (key === 'regoCertificate') {
          type = REGO_CERTIFICATE_DATA_EDIT_TYPE;
        }
        if (key === 'bankStatement') {
          type = BANK_STATEMENT_DATA_EDIT_TYPE;
        }
        if (key === 'dataEdits') {
          return resolveMenuItemFlag(
            key,
            resolveVehicleDataEditsStatus(items),
            'DATA_EDITS',
            item.value,
            items?.vin,
            registrationPaperStatus,
            items?.rules
          );
        }
        const isNotMatch =
          item.value === '2' &&
          key === 'vin' &&
          [
            items?.vin?.licencePlateMatch,
            items?.licencePlate?.vinMatch
          ].includes('DIFFERENT');

        const status = resolveSecurityCheckStatus(
          documentObj,
          type,
          items,
          key,
          isNotMatch
        );
        return resolveMenuItemFlag(
          key,
          status,
          type,
          item.value,
          documentObj,
          registrationPaperStatus,
          items?.rules
        );
      });

    // Empty check list (cert/payout/ownership/marine rego) → SUCCESS when docs complete.
    // All checks filtered out → INFO so we never show a false green tick.
    if (flags.length === 0) {
      return checkKeys.length === 0
        ? DETAIL_VIEW_FLAG_STATUS.SUCCESS
        : DETAIL_VIEW_FLAG_STATUS.INFO;
    }

    return aggregateSectionFlagSeverity(flags);
  };
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollToItem = (index: number) => {
    // Get the item element by index
    if (isMobile) {
      const itemElement = containerRef.current?.children[index] as HTMLElement;
      // Scroll the container to the item element
      itemElement?.scrollIntoView({ behavior: 'smooth', inline: 'start' });
    }
  };
  return (
    <>
      <div className="xl:flex w-full">
        <div className="flex flex-col min-h-[160px] detail-box justify-start items-center xl:hidden">
          <SemiCircleProgress
            percentage={
              parseFloat((countOfAll.done / countOfAll.total).toFixed(2)) * 100
            }
            size={{
              width: 200,
              height: 200
            }}
            strokeWidth={10}
            bgStrokeColor={'#EAECF0'}
            hasBackground={true}
            strokeColor={'#F63D68'}
          />
          <div className="mt-[-120px] flex flex-col justify-center items-center">
            <p style={{ fontSize: 12, color: '#475467' }}>Progress</p>
            <p
              style={{
                fontSize: 16,
                color: '#101828'
              }}>{`${countOfAll.done} of ${countOfAll.total}`}</p>
          </div>
        </div>
        <div
          ref={containerRef}
          className="xl:flex-shrink-0 flex flex-row xl:flex-col xl:flex-col lg:min-w-[170px] xl:w-full overflow-auto mb-4 items-center">
          {showActivityHistory && (
            <div className="flow w-full">
              <StatusListItem
                value={ACTIVITY_HISTORY_STEP}
                label="Activity History"
                onClick={() => {
                  scrollToItem(0);
                  setActiveStep(ACTIVITY_HISTORY_STEP);
                }}
                active={activeStep === ACTIVITY_HISTORY_STEP}
                hideIcon
                className="justify-between"
              />
            </div>
          )}
          {DETAIL_VIEW_ITEMS.map((item, index) => {
            const countObj = countOfDoneElements(index);
  
            const showFlg = () => {
              switch (item.keyPath) {
                case 'driverLicense':
                  return (
                    _intersection(documentTypes, [
                      LenderDocumentTypeEnum.SELLER_ID,
                      LenderDocumentTypeEnum.DRIVER_LICENCE_FRONT,
                      LenderDocumentTypeEnum.DRIVER_LICENCE_BACK,
                      LenderDocumentTypeEnum.FACE
                    ]).length > 0
                  );
                case 'vehicle':
                  return hasVehicleDetailsSectionDocument(
                    inspectionDetailData.documentTypes
                  );
                case 'regoPaper':
                  return (
                    documentTypes.includes(
                      LenderDocumentTypeEnum.REGO_PAPERS
                    ) ||
                    documentTypes.includes(
                      LenderDocumentTypeEnum.MARINE_REGO_CERT
                    )
                  );
                case 'bank':
                  return documentTypes.includes(
                    LenderDocumentTypeEnum.BANK_STATEMENTS
                  );
                case 'cert':
                  return (
                    (documentTypes.includes(
                      LenderDocumentTypeEnum.ROADWORTHY_CERT
                    ) &&
                      (inspectionDetailData?.inspectionChecks?.registration
                        ?.status === InspectionSecurityStatusEnum.noRegister ||
                        inspectionDetailData?.inspectionChecks?.registration
                          ?.status === InspectionSecurityStatusEnum.expired) &&
                      inspectionDetailData.inspectionConfig
                        ?.roadworthyIfUnregistered) ||
                    (documentTypes.includes(
                      LenderDocumentTypeEnum.ROADWORTHY_CERT
                    ) &&
                      inspectionDetailData.inspectionConfig
                        ?.roadworthyByState &&
                      ROADWORTHY_BY_STATE.includes(inspectionDetailData.state))
                  );
                case 'payoutLetter':
                  return shouldShowPayoutLetterSection(
                    inspectionDetailData.documentTypes,
                    inspectionDetailData.inspectionChecks,
                    inspectionDetailData.assetType
                  );
                case 'ownershipProof':
                  return documentTypes.includes(
                    LenderDocumentTypeEnum.OWNERSHIP_PROOF
                  );
                default:
                  return false;
              }
            };
            const flag = flagStatus(item, index);
            const docsComplete =
              countObj.total > 0 && countObj.done === countObj.total;
            const indicator = getSectionIndicatorState(flag, docsComplete);
            return (
              <div
                key={index}
                className={clsx('flow w-full', !showFlg() && 'hidden')}>
                <StatusListItem
                  value={item.value}
                  label={
                    item.value === '2'
                      ? getAssetSpecificContent(
                          inspectionDetailData.assetType as AssetType
                        ).title + ' Details'
                      : item.shortLabel
                  }
                  onClick={() => {
                    scrollToItem(showActivityHistory ? index + 1 : index);
                    setActiveStep(item.value);
                  }}
                  progressLabel={countObj.done + '/' + countObj.total}
                  active={activeStep === item.value}
                  success={indicator.success}
                  alertVariant={indicator.alertVariant}
                  hideIcon={
                    (data.status === InspectionStatusEnum.Initiated &&
                      item.value !== '5') ||
                    indicator.hideIcon
                  }
                  className="justify-between"
                />
              </div>
            );
          })}
          <div className="hidden xl:block px-6 py-8 gap-6">
            <CircularProgressbarWithChildren
              value={countOfAll.done}
              maxValue={countOfAll.total}
              strokeWidth={10}
              styles={buildStyles({
                pathColor: '#F63D68',
                textColor: '#101828',
                trailColor: '#EAECF0'
              })}>
              <div style={{ fontSize: 12, color: '#475467' }}>PROGRESS</div>
              <div style={{ fontSize: 20, color: '#101828' }}>
                <strong>{`${countOfAll.done} of ${countOfAll.total}`}</strong>
              </div>
            </CircularProgressbarWithChildren>
          </div>
        </div>
      </div>
    </>
  );
};
export default DetailedInfo;
